import { Routes, Route, Navigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useSession } from "@/lib/auth-client";
import { AuthProvider, useAuthContext } from "@/components/auth";
import { TenantThemeProvider } from "@/theme/TenantThemeProvider";
import { DashboardLayout } from "@/components/templates/DashboardLayout";
import { Dashboard } from "@/pages/Dashboard";
import { ContactsList } from "@/pages/Contacts/ContactsList";
import { ContactCreate } from "@/pages/Contacts/ContactCreate";
import { ContactEdit } from "@/pages/Contacts/ContactEdit";
import { ProspectsList } from "@/pages/Prospects/ProspectsList";
import { ReportHistory } from "@/pages/Reports/ReportHistory";
import { InvitePage } from "@/pages/Invite";
import { UnsubscribePage } from "@/pages/Unsubscribe";
import { NoOrganization } from "@/pages/NoOrganization";
import { LoginPage } from "@/pages/Login";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import {
  AdminTenants,
  AdminUsers,
  AdminJobs,
  AdminApprovals,
} from "@/pages/admin";
import { TemplateSettings } from "@/pages/Templates";
import { TeamManagement } from "@/pages/Team/TeamManagement";

function LoadingScreen() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// Wrapper for org-admin routes (team management)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuthContext();
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Wrapper for super-admin-only routes (system-level: tenants, users, jobs)
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin } = useAuthContext();
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AuthenticatedRoutes() {
  const { status, tenant } = useAuthContext();

  if (status === "loading") {
    return <LoadingScreen />;
  }

  if (status === "no_organization") {
    return <NoOrganization />;
  }

  return (
    <TenantThemeProvider tenantSlug={tenant?.slug}>
      <Routes>
        <Route path="/" element={<DashboardLayout />}>
          {/* Common routes - all authenticated users */}
          <Route index element={<Dashboard />} />
          <Route path="contacts" element={<ContactsList />} />
          <Route path="contacts/new" element={<ContactCreate />} />
          <Route path="contacts/:id" element={<ContactEdit />} />
          <Route path="prospects" element={<ProspectsList />} />
          <Route path="reports/history" element={<ReportHistory />} />
          <Route path="templates" element={<TemplateSettings />} />

          {/* Admin routes - same layout, protected by AdminRoute */}
          <Route
            path="team"
            element={
              <AdminRoute>
                <TeamManagement />
              </AdminRoute>
            }
          />
          <Route
            path="tenants"
            element={
              <SuperAdminRoute>
                <AdminTenants />
              </SuperAdminRoute>
            }
          />
          <Route
            path="users"
            element={
              <SuperAdminRoute>
                <AdminUsers />
              </SuperAdminRoute>
            }
          />
          <Route
            path="jobs"
            element={
              <SuperAdminRoute>
                <AdminJobs />
              </SuperAdminRoute>
            }
          />
          <Route
            path="approvals"
            element={
              <SuperAdminRoute>
                <AdminApprovals />
              </SuperAdminRoute>
            }
          />
        </Route>

        {/* Redirect old /admin routes to new locations */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route
          path="/admin/tenants"
          element={<Navigate to="/tenants" replace />}
        />
        <Route path="/admin/users" element={<Navigate to="/users" replace />} />
        <Route path="/admin/jobs" element={<Navigate to="/jobs" replace />} />
        <Route
          path="/admin/approvals"
          element={<Navigate to="/approvals" replace />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </TenantThemeProvider>
  );
}

function ProtectedApp() {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return <LoadingScreen />;
  }

  if (!session) {
    return <LoginPage />;
  }

  return (
    <AuthProvider>
      <AuthenticatedRoutes />
    </AuthProvider>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />

      {/* All other routes require authentication */}
      <Route path="*" element={<ProtectedApp />} />
    </Routes>
  );
}
